import { apiService } from "api/apiService";
import { consumeIntendedPath } from "core/auth/intendedPath";
import {
  createUnAuthenticatedUser,
  type UnAuthenticatedUser,
} from "models/auth/unAuthenticatedUser";
import { defineStore } from "pinia";
import { ref } from "vue";

export const TWO_FACTOR_STATUS_CODE = 412;
export const TWO_FACTOR_STATUS_MESSAGE = "2FA required";
export const UNIQUE_DEVICE_CODE_KEY = "unique_device_code";

/**
 * Authorization Store
 *
 * This store manages the roles used in the application.
 */
export const useAuthenticationStore = defineStore("authentication", () => {
  // State
  const user = ref<object | null>(null);
  const unAuthenticatedUser = ref<UnAuthenticatedUser>(
    createUnAuthenticatedUser(),
  );
  const status = ref<{ loggedIn: boolean }>({ loggedIn: false });
  const errors = ref([]);

  const setErrorMessages = (errorMessages) => {
    setErrors(errorMessages);
  };

  const loginUser = async (pin_code?: string) => {
    const {
      email = null,
      password = null,
      remember_me = null,
    } = unAuthenticatedUser.value;

    if (!email || !password) {
      throw new Error("E-mail en wachtwoord zijn verplicht");
    }

    const unique_device_code: string = getUniqueDeviceCode();

    const { data: loginResponse } = await apiService.post(
      "/oauth/login",
      {
        email,
        password,
        remember_me,
        unique_device_code,
        ...(pin_code && { pin_code }),
      },
      {
        withCredentials: true,
      },
    );

    processResults(loginResponse);
  };

  const processResults = (result, redirectPath: string | null = null) => {
    // The login response carries two kinds of data: the user's profile, which
    // is plain app state, and the OAuth tokens. Keep them apart so it is clear
    // which localStorage writes hold credentials.
    const {
      user: profile,
      access_token: accessToken,
      refresh_token: refreshToken,
    } = result;

    localStorage.setItem("user_name", profile.username);
    localStorage.setItem("first_name", profile.first_name);
    localStorage.setItem("user", JSON.stringify(profile));
    localStorage.setItem("school_id", profile.school_id);
    localStorage.setItem("school_name", profile.school_name);
    localStorage.setItem("tool", profile.tool);
    localStorage.setItem("school_hash", profile.school_hash);
    localStorage.setItem("show_nps", result.show_nps);

    let role_user_id = profile.role_user_id;
    if (role_user_id) {
      let roles = profile.role;

      if (
        roles.includes("teacher") ||
        roles.includes("decaan") ||
        roles.includes("teamleader") ||
        roles.includes("organizer") ||
        roles.includes("manager")
      ) {
        localStorage.setItem("employee_id", role_user_id);
        localStorage.removeItem("student_id");
        localStorage.removeItem("student_user_id");
      } else if (roles.includes("student")) {
        localStorage.setItem("student_id", role_user_id);
        localStorage.removeItem("employee_id");
      }
    }

    // Bearer tokens live in localStorage by design (SPA without a cookie
    // session); accepted risk, flagged by code scanning as clear-text storage.
    localStorage.setItem("user_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);

    const currentDateTime = new Date().getTime();
    const expiryDate = new Date(
      currentDateTime + parseInt(result.expires_in) * 1000,
    );

    localStorage.setItem("expires_in", expiryDate.toLocaleString());

    if (result.schools) {
      localStorage.setItem("school_choices", JSON.stringify(result.schools));
      localStorage.setItem("menu_locations", JSON.stringify(result.schools));
    }

    setUser(result);

    if (redirectPath == null) {
      // With a school still to choose, the remembered page is kept for the
      // login that follows the choice.
      if (result.schools) {
        return (window.location.href = "/");
      }

      return (window.location.href = consumeIntendedPath() ?? "/");
    }

    window.location.href = redirectPath;
  };

  // Mutations (refactored as functions)
  const setErrors = (data) => {
    errors.value = data;
  };

  const setUser = (data) => {
    status.value.loggedIn = true;
    user.value = data;

    // Reset unAuthenticatedUser
    unAuthenticatedUser.value = createUnAuthenticatedUser();
  };

  const generateUUID = (): string => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    // Fallback for non-secure contexts (e.g., HTTP)
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
      const random = (Math.random() * 16) | 0;
      const value = char === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  };

  const getUniqueDeviceCode = (): string => {
    let uniqueDeviceCode = localStorage.getItem(UNIQUE_DEVICE_CODE_KEY);

    if (uniqueDeviceCode === null) {
      uniqueDeviceCode = generateUUID();
      localStorage.setItem(UNIQUE_DEVICE_CODE_KEY, uniqueDeviceCode);
    }

    return uniqueDeviceCode;
  };

  return {
    user,
    unAuthenticatedUser,
    status,
    errors,
    setErrorMessages,
    loginUser,
    processResults,
  };
});
